import React, { useState, useEffect, useCallback, useRef } from 'react';
import SearchPlayers from '../SearchPlayers/SearchPlayers';
import latinize from 'latinize';
import html2canvas from 'html2canvas';
import defaultPlayerImg from '../../img/defaultPlayer.jpg';
import { getPossibleSolutionsArg } from '../../services/api';
import { checkPlayer } from '../../helpers/checkPlayer';
import { getImgSrc } from '../../helpers/getImageSrc';
import { prettyClubName } from '../TeamSelector/TeamSelector';
import './PlayerCombiner.css';

const backendURL = (process.env.REACT_APP_BACKEND_URL || 'https://footy-tictactoe-production.up.railway.app');

// Fuentes de fotos que NO mandan Access-Control-Allow-Origin (confirmado con curl): sin CORS,
// html2canvas no puede leerlas para dibujarlas en el canvas de "Descargar Imagen" (quedan en
// blanco), aunque en pantalla se vean bien (ver getImageSrc.js — la vista normal no usa
// crossOrigin, por eso no le afecta esto). Se resuelven server-to-server vía /players/img-proxy.
const IMG_PROXY_HOSTS = new Set([
    'images.footy-games.com.ar',
    'img.a.transfermarkt.technology',
    'img.sofascore.com',
    'cdn.resfu.com',
    'upload.wikimedia.org',
]);

const toProxiedImgSrc = (absoluteSrc) => {
    try {
        const u = new URL(absoluteSrc);
        if (u.protocol === 'https:' && IMG_PROXY_HOSTS.has(u.hostname)) {
            return `${backendURL}/players/img-proxy?url=${encodeURIComponent(u.toString())}`;
        }
    } catch (e) {
        // src local (asset de webpack) u otra URL no reconocida: no hace falta proxearla.
    }
    return null;
};

// Función que reparte equitativamente valores en un rango
const calculateEvenPositions = (count, start, end) => {
    if (count === 1) return [(start + end) / 2];
    const spacing = (end - start) / (count - 1);
    return Array.from({ length: count }, (_, i) => start + spacing * i);
};

// Las posiciones se agrupan en LÍNEAS (como una pizarra táctica real) en vez de darle a
// cada sigla su propia coordenada suelta. Antes LM/CM/RM vivían en alturas distintas y con
// rangos horizontales que se cruzaban, así que en formaciones como 3-5-2 la ficha del CM
// terminaba encima de la del RM. Repartiendo cada línea completa de una sola vez, dos
// jugadores de la misma línea nunca pueden solaparse.
// El orden dentro de cada línea ya es izquierda → centro → derecha.
const FORMATION_BANDS = [
    { top: 92, keys: ['GK'] },
    { top: 74, keys: ['LB', 'CB', 'RB'] },
    { top: 62, keys: ['CDM'] },
    { top: 50, keys: ['LM', 'CM', 'RM'] },
    { top: 38, keys: ['CAM'] },
    { top: 18, keys: ['LW', 'ST', 'RW'] },
];
const WIDE_KEYS = new Set(['LB', 'LM', 'LW', 'RB', 'RM', 'RW']);

const calculateFormationPositions = (positions) => {
    const coords = {};
    // Índice por posición para ir consumiendo los slots de cada sigla en orden.
    const pushCoord = (key, top, left) => {
        (coords[key] = coords[key] || []).push({ top, left });
    };

    FORMATION_BANDS.forEach(({ top, keys }) => {
        // Jugadores de esta línea, ya ordenados de izquierda a derecha.
        const slots = [];
        keys.forEach((key) => {
            for (let i = 0; i < (positions[key] || 0); i++) slots.push(key);
        });
        if (slots.length === 0) return;

        // Si la línea tiene carrileros/extremos se usa todo el ancho; si es solo gente por
        // el medio, se mantiene compacta para que no parezca una línea de cinco.
        const hasWide = slots.some((k) => WIDE_KEYS.has(k));
        const [from, to] = hasWide
            ? [10, 90]
            : (slots.length >= 3 ? [26, 74] : [33, 67]);

        calculateEvenPositions(slots.length, from, to)
            .forEach((left, i) => pushCoord(slots[i], top, left));
    });

    // Red de seguridad: si alguna formación trae una sigla que no está en ninguna línea,
    // se ubica al medio en vez de desaparecer de la cancha.
    Object.keys(positions).forEach((key) => {
        const faltan = (positions[key] || 0) - (coords[key]?.length || 0);
        for (let i = 0; i < faltan; i++) pushCoord(key, 55, 50);
    });

    return coords;
};

const PlayerCombinerArg = ({ selectedTeam, positions, formationName = '', onResetGame = () => { } }) => {
    const fieldRef = useRef(null);

    const initializeLineup = () => {
        const init = {};
        Object.keys(positions).forEach(pos => {
            init[pos] = [];
        });
        return init;
    };

    const [lineup, setLineup] = useState(initializeLineup());
    const [teamOne, setTeamOne] = useState('');
    const [teamTwo, setTeamTwo] = useState('');
    const [error, setError] = useState('');
    const [gameWon, setGameWon] = useState(false);
    const [skipCount, setSkipCount] = useState(5);
    const [helpMessage, setHelpMessage] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [idPlayersOnField, setIdPlayersOnField] = useState([]);
    const [lastQuery, setLastQuery] = useState(null);
    const [currentSuggestions, setCurrentSuggestions] = useState([]);
    const [suggestionIndex, setSuggestionIndex] = useState(0);
    const [freePositionsOnField, setFreePositionsOnField] = useState([]);
    const [isLoadingTeam, setIsLoadingTeam] = useState(false);
    const [descargando, setDescargando] = useState(false);

    const totalRequiredPlayers = Object.values(positions).reduce((acc, curr) => acc + curr, 0);
    const totalPlayers = Object.values(lineup).reduce((acc, curr) => acc + curr.length, 0);
    const [shouldRandomize, setShouldRandomize] = useState(false);
    const formationPositions = calculateFormationPositions(positions);
    const lastQueryRef = useRef(null);
    const lastResponseDataRef = useRef(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (positions && Object.keys(positions).length > 0) {
            const updatedPositions = Object.entries(positions).flatMap(([position, count]) =>
                Array(count).fill(position)
            );
            setFreePositionsOnField(updatedPositions);
        }
    }, [positions]);

    useEffect(() => {
        if (totalPlayers === totalRequiredPlayers) {
            setGameWon(true);
        }
    }, [totalPlayers, totalRequiredPlayers]);

    const randomizeTeams = useCallback(() => {
        if (!freePositionsOnField.length || !idPlayersOnField) {
            return; // Esperar a que los datos estén disponibles
        }
        // Activamos el estado de loading
        setIsLoadingTeam(true);
        let info = {};
        if (selectedTeam === 'allLPF') {
            info = {
                freePositions: freePositionsOnField,
                excluded: idPlayersOnField
            };
        } else {
            if (!selectedTeam) return;
            setTeamOne(selectedTeam);
            info = {
                team: selectedTeam,
                freePositions: freePositionsOnField,
                excluded: idPlayersOnField
            };
        }

        const infoString = JSON.stringify(info);
        // Si la consulta es idéntica a la anterior y ya tenemos datos, usar el último resultado
        if (lastQueryRef.current === infoString && lastResponseDataRef.current) {
            if (selectedTeam === 'allLPF') {
                if (lastResponseDataRef.current.qualifyingItems?.length > 0) {
                    const randomIndex = Math.floor(Math.random() * lastResponseDataRef.current.qualifyingItems.length);
                    const [t1, t2] = lastResponseDataRef.current.qualifyingItems[randomIndex];
                    setTeamOne(t1);
                    setTeamTwo(t2);
                }
            } else {
                if (lastResponseDataRef.current.qualifyingItems && Object.keys(lastResponseDataRef.current.qualifyingItems).length > 0) {
                    const keys = Object.keys(lastResponseDataRef.current.qualifyingItems);
                    const randomIndex = Math.floor(Math.random() * keys.length);
                    const t2 = keys[randomIndex];
                    setTeamTwo(t2);
                }
            }
            setHelpMessage('');
            setIsLoadingTeam(false);
            return;
        } else {
            lastQueryRef.current = infoString;
            getPossibleSolutionsArg(info)
                .then((data) => {
                    lastResponseDataRef.current = data;
                    if (selectedTeam === 'allLPF') {
                        if (data?.qualifyingItems?.length > 0) {
                            const randomIndex = Math.floor(Math.random() * data.qualifyingItems.length);
                            const [t1, t2] = data.qualifyingItems[randomIndex];
                            setTeamOne(t1);
                            setTeamTwo(t2);
                        }
                    } else {
                        if (data?.qualifyingItems && Object.keys(data.qualifyingItems).length > 0) {
                            const keys = Object.keys(data.qualifyingItems);
                            const randomIndex = Math.floor(Math.random() * keys.length);
                            const t2 = keys[randomIndex];
                            setTeamTwo(t2);
                        }
                    }
                    setHelpMessage('');
                })
                .catch((error) => {
                    console.error("Error al obtener posibles soluciones:", error);
                })
                .finally(() => {
                    setIsLoadingTeam(false);
                });
        }
    }, [selectedTeam, freePositionsOnField, idPlayersOnField]);

    useEffect(() => {
        if (!freePositionsOnField.length || !idPlayersOnField) {
            return; // Esperar a que los datos estén disponibles
        } else {
            if (!hasInitialized.current) {
                hasInitialized.current = true;
                randomizeTeams();
            }
        }
    }, [randomizeTeams, freePositionsOnField, idPlayersOnField]);

    // useEffect que se ejecuta cada vez que freePositionsOnField cambia
    useEffect(() => {
        if (shouldRandomize) {
            randomizeTeams();
            setShouldRandomize(false); // Reiniciamos el trigger para evitar llamadas repetidas
        }
    }, [freePositionsOnField, shouldRandomize, randomizeTeams]);

    const resetGame = () => {
        setLineup(initializeLineup());
        setError('');
        setGameWon(false);
        setSkipCount(3);
        setHelpMessage('');
        setSelectedPlayer(null);
        onResetGame();
    };

    const handlePlayerSelect = (player) => {
        if (Object.values(lineup).flat().some(p => p._id === player._id)) {
            setError('El jugador ya está en la formación');
            return;
        }
        if (!checkPlayer(player, teamOne) || !checkPlayer(player, teamTwo)) {
            setError('El jugador no cumple con los requisitos');
            return;
        }
        const availablePositions = player.specificPosition.filter(
            pos => positions[pos] !== undefined && (lineup[pos]?.length ?? 0) < positions[pos]
        );
        if (availablePositions.length === 0) {
            setError('No hay espacio para este jugador en la formación');
            return;
        }
        if (availablePositions.length === 1) {
            const selectedPos = availablePositions[0];
            setLineup(prev => ({
                ...prev,
                [selectedPos]: [...(prev[selectedPos] || []), player]
            }));
            setIdPlayersOnField(prev => [...prev, player._id]);
            setFreePositionsOnField(prev => {
                const index = prev.indexOf(selectedPos);
                if (index === -1) return prev;
                return [...prev.slice(0, index), ...prev.slice(index + 1)];
            });
            setShouldRandomize(true);
            // Scroll suave hacia arriba
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setSelectedPlayer({ player, availablePositions });
        }
        setError('');
        setHelpMessage('');
    };

    const handleConfirmPosition = (position) => {
        setLineup(prev => ({
            ...prev,
            [position]: [...(prev[position] || []), selectedPlayer.player]
        }));
        setIdPlayersOnField(prev => [...prev, selectedPlayer.player._id]);
        setFreePositionsOnField(prev => {
            const index = prev.indexOf(position);
            if (index === -1) return prev;
            return [...prev.slice(0, index), ...prev.slice(index + 1)];
        });
        setSelectedPlayer(null);
        setShouldRandomize(true);
        // Scroll suave hacia arriba
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSkip = () => {
        if (skipCount <= 0) return;
        setSkipCount(prev => prev - 1);
        randomizeTeams();
    };

    const handleHelp = async () => {
        const missingPositions = Object.keys(positions).filter(
            key => (lineup[key]?.length ?? 0) < positions[key]
        );

        if (missingPositions.length === 0) {
            setHelpMessage('La formación ya está completa.');
            return;
        }

        const currentQuery = {
            teamOne: teamOne.toLowerCase(),
            teamTwo: teamTwo.toLowerCase(),
            missingPositions: missingPositions.sort() // Ordenamos para comparar fácilmente
        };

        // Si ya hicimos la consulta, solo iteramos sobre los resultados
        if (
            lastQuery &&
            JSON.stringify(currentQuery) === JSON.stringify(lastQuery) &&
            currentSuggestions.length > 0
        ) {
            const suggestion = currentSuggestions[suggestionIndex % currentSuggestions.length];
            setHelpMessage(`${suggestion.nombre} puede ser agregado como ${suggestion.missingPos}`);
            setSuggestionIndex(suggestionIndex + 1);
            return;
        }

        // Si es una nueva consulta, reiniciamos los estados
        setLastQuery(currentQuery);
        setSuggestionIndex(0);

        try {
            const customLatinize = (text) =>
                text.split(/(ñ)/gi).map(part => part.toLowerCase() === 'ñ' ? part : latinize(part)).join('');

            const response = await fetch(
                `${backendURL}/players/searchByCriteria?item1=${customLatinize(teamOne)}&item2=${customLatinize(teamTwo)}&league=arg`
            );
            const data = await response.json();

            // Filtramos y asignamos la posición correcta
            const newSuggestions = [];
            missingPositions.forEach(pos => {
                const filteredPlayers = data
                    .filter(player => player.specificPosition.includes(pos))
                    .map(player => ({ ...player, missingPos: pos }));
                newSuggestions.push(...filteredPlayers);
            });

            if (newSuggestions.length === 0) {
                setHelpMessage('No se encontró sugerencia para ninguna posición faltante.');
                setCurrentSuggestions([]);
                return;
            }

            // Guardamos las sugerencias y mostramos la primera
            setCurrentSuggestions(newSuggestions);
            setHelpMessage(`${newSuggestions[0].nombre} puede ser agregado como ${newSuggestions[0].missingPos}`);
            setSuggestionIndex(1);
        } catch (err) {
            console.error(err);
            setHelpMessage('Error al obtener sugerencias.');
        }
    };

    // Función para descargar la formación como imagen
    const handleShareFormation = async () => {
        if (!fieldRef.current || descargando) return;
        setDescargando(true);
        try {
            // Las fotos vienen de CDNs que no mandan Access-Control-Allow-Origin, así que
            // html2canvas no puede dibujarlas: se bajan por NUESTRO proxy (mismo origen) y se
            // pasan a data: URL.
            const imgs = [...fieldRef.current.querySelectorAll('.build11-player-img')];
            const originalSrc = new Map();
            // Si una foto no se puede resolver por el proxy y encima es de otro origen,
            // html2canvas no puede leerla (canvas "tainted") y saldría como un círculo vacío.
            // En ese caso se usa el avatar por defecto, que es un asset propio.
            const usarDefault = (img) => {
                originalSrc.set(img, img.src);
                img.src = defaultPlayerImg;
            };
            const esOtroOrigen = (src) => {
                try { return new URL(src, window.location.href).origin !== window.location.origin; }
                catch (e) { return false; }
            };

            await Promise.all(imgs.map(async (img) => {
                const proxied = toProxiedImgSrc(img.src);
                if (!proxied) {
                    if (esOtroOrigen(img.src) || !img.naturalWidth) usarDefault(img);
                    return;
                }
                try {
                    // Timeout por foto: el proxy va contra CDNs externos y alguno puede
                    // tardar muchísimo. Sin esto, una sola foto lenta dejaba la descarga
                    // colgada >45s (medido). Si se pasa, esa foto usa el avatar por defecto
                    // y la formación se descarga igual.
                    const ac = new AbortController();
                    const t = setTimeout(() => ac.abort(), 6000);
                    let response;
                    try { response = await fetch(proxied, { signal: ac.signal }); }
                    finally { clearTimeout(t); }
                    // Sin este chequeo, un 404 del CDN (pasa: hay fotos que ya no existen en
                    // el mirror) se convertía igual a data: URL — pero con el CUERPO DEL ERROR
                    // adentro, así que el jugador salía con la foto en blanco en el PNG. Si la
                    // respuesta no es una imagen, se deja el <img> como está: en pantalla ya
                    // cayó a defaultPlayer.jpg por el onError, que es del mismo origen y se
                    // captura sin problema.
                    const tipo = response.headers.get('content-type') || '';
                    if (!response.ok || !tipo.startsWith('image/')) { usarDefault(img); return; }
                    const blob = await response.blob();
                    const dataUrl = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                    // Se intercambia el src EN EL DOM REAL (guardando el original para dejarlo
                    // como estaba) y se espera el decode. Antes esto se hacía dentro de onclone,
                    // pero ahí html2canvas ya no espera a que la nueva imagen cargue y las fotos
                    // salían en blanco en el PNG. Visualmente no se nota: es la misma foto.
                    originalSrc.set(img, img.src);
                    img.src = dataUrl;
                    if (img.decode) await img.decode().catch(() => {});
                } catch (e) {
                    // Si falla la descarga, se cae al avatar por defecto en vez de dejar el
                    // círculo vacío en el PNG.
                    usarDefault(img);
                }
            }));

            let canvas;
            try {
                canvas = await html2canvas(fieldRef.current, { useCORS: true });
            } finally {
                // Volver a las URLs originales pase lo que pase, para no dejar la pantalla
                // con data: URLs pesadas en memoria.
                originalSrc.forEach((src, img) => { img.src = src; });
            }
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error('No se pudo crear el blob');
                    return;
                }
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'formation.png';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }, 'image/png');
        } catch (error) {
            console.error('Error al capturar la formación:', error);
        } finally {
            setDescargando(false);
        }
    };

    const teamImages = require.context('../../img', true, /\.jpg$/).keys()
        .reduce((acc, key) => ({
            ...acc,
            [key.replace('./', '').replace('.jpg', '')]: require(`../../img/${key.replace('./', '')}`)
        }), {});

    const getTeamImage = (teamName) => {
        const key = Object.keys(teamImages).find(key => key.endsWith(teamName));
        return key ? teamImages[key] : null;
    };

    // Tarjeta de un equipo del cruce (escudo + nombre), con skeleton mientras carga.
    const DuelTeam = ({ team }) => (
        <div className="b11-duel-team">
            {isLoadingTeam || !team ? (
                <div className="b11-duel-skeleton" />
            ) : (
                <>
                    <img className="b11-duel-crest" src={getTeamImage(team)} alt="" />
                    <span className="b11-duel-name">{prettyClubName(team)}</span>
                </>
            )}
        </div>
    );

    return (
        <div className="b11-shell">
            {/* ── HUD: formación, progreso y skips siempre visibles ── */}
            <div className="b11-hud">
                <div className="b11-hud-block">
                    <span className="b11-hud-label">Formación</span>
                    <span className="b11-hud-value is-gold">{formationName || '—'}</span>
                </div>
                <div className="b11-hud-sep" />
                <div className="b11-hud-block b11-progress">
                    <span className="b11-hud-label">Once</span>
                    <span className="b11-hud-value">{totalPlayers}/{totalRequiredPlayers}</span>
                    <div className="b11-progress-track">
                        <div
                            className="b11-progress-fill"
                            style={{ width: `${totalRequiredPlayers ? (totalPlayers / totalRequiredPlayers) * 100 : 0}%` }}
                        />
                    </div>
                </div>
                <div className="b11-hud-sep" />
                <div className="b11-hud-block">
                    <span className="b11-hud-label">Skips</span>
                    <span className="b11-hud-value">{skipCount}</span>
                </div>
                <div className="b11-hud-spacer" />
                <button type="button" className="b11-exit-btn" onClick={resetGame}>
                    Salir
                </button>
            </div>

            <div className="b11-game">
                {/* El cruce va ANTES de la cancha en el DOM: en móvil (una sola columna) es lo
                    primero que tenés que ver para saber a quién buscar. En escritorio el grid
                    lo reubica arriba a la derecha. */}
                <div className="b11-challenge">
                    <span className="b11-challenge-label">¿Quién jugó en los dos?</span>
                    <div className="b11-duel">
                        <DuelTeam team={teamOne} />
                        <span className="b11-vs">VS</span>
                        <DuelTeam team={teamTwo} />
                    </div>
                </div>

                {/* ── Cancha ── */}
                <div className="b11-pitch-wrap">
                    <div className="field-player" ref={fieldRef}>
                        {/* Líneas de cal */}
                        <div className="b11-mark b11-mark--pitch" />
                        <div className="b11-mark b11-mark--half" />
                        <div className="b11-mark b11-mark--circle" />
                        <div className="b11-mark b11-mark--spot b11-mark--spot-c" />
                        <div className="b11-mark b11-mark--spot b11-mark--spot-b" />
                        <div className="b11-mark b11-mark--spot b11-mark--spot-t" />
                        <div className="b11-mark b11-mark--pen b11-mark--pen-b" />
                        <div className="b11-mark b11-mark--pen b11-mark--pen-t" />
                        <div className="b11-mark b11-mark--goal b11-mark--goal-b" />
                        <div className="b11-mark b11-mark--goal b11-mark--goal-t" />
                        <div className="b11-mark b11-mark--corner b11-mark--corner-tl" />
                        <div className="b11-mark b11-mark--corner b11-mark--corner-tr" />
                        <div className="b11-mark b11-mark--corner b11-mark--corner-bl" />
                        <div className="b11-mark b11-mark--corner b11-mark--corner-br" />

                        {Object.entries(formationPositions).map(([position, spots]) =>
                            spots.map((spot, index) => {
                                const isFilled = lineup[position] && lineup[position][index];
                                const isAvailableForSelected = selectedPlayer && selectedPlayer.availablePositions.includes(position) && !isFilled;
                                return (
                                    <div
                                        key={`${position}-${index}`}
                                        className={`position-container ${isAvailableForSelected ? 'highlight-available' : ''}`}
                                        style={{ top: `${spot.top}%`, left: `${spot.left}%` }}
                                        onClick={isAvailableForSelected ? () => handleConfirmPosition(position) : undefined}
                                    >
                                        {isFilled ? (
                                            <div className="player-info">
                                                <div className="build11-player-img-container">
                                                    {/* Sin crossOrigin: ni el mirror R2 (images.footy-games.com.ar)
                                                        ni img.a.transfermarkt.technology mandan
                                                        Access-Control-Allow-Origin, así que con
                                                        crossOrigin="anonymous" el navegador rechazaba la carga
                                                        (onError caía a la imagen default) para la mayoría de los
                                                        jugadores. El html2canvas de "Descargar Imagen" resuelve
                                                        las fotos aparte, vía /players/img-proxy + onclone. */}
                                                    <img
                                                        className="build11-player-img"
                                                        src={getImgSrc(lineup[position][index].img) || defaultPlayerImg}
                                                        alt={lineup[position][index].nombre}
                                                        onError={e => { e.currentTarget.src = defaultPlayerImg; }}
                                                    />
                                                    <span className="b11-token-pos">{position}</span>
                                                </div>
                                                <div className="build11-player-name-container">
                                                    <span className="build11-player-name">{lineup[position][index].nombre}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="position-rectangle">
                                                <span className="position-label">{position}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* ── Panel de juego ── */}
                <div className="b11-rail">
                    {selectedPlayer ? (
                        <div className="selection-message">
                            <p>Elegí la posición de <strong>{selectedPlayer.player.nombre}</strong></p>
                            <p>Tocá uno de los círculos verdes de la cancha</p>
                        </div>
                    ) : (
                        !gameWon && (
                            <SearchPlayers
                                onSelect={handlePlayerSelect}
                                turn={1}
                                player={1}
                                league="arg"
                                placeholder="Buscar jugador…"
                            />
                        )
                    )}

                    {error && <div className="b11-error">{error}</div>}
                    {helpMessage && <p className="b11-hint">💡 {helpMessage}</p>}

                    {!gameWon && (
                        <div className="b11-actions">
                            <button
                                type="button"
                                className="b11-btn"
                                onClick={handleHelp}
                                disabled={isLoadingTeam}
                            >
                                💡 Pista
                            </button>
                            <button
                                type="button"
                                className="b11-btn"
                                onClick={handleSkip}
                                disabled={skipCount <= 0 || isLoadingTeam}
                                title={skipCount <= 0 ? 'No te quedan skips' : 'Cambiar el cruce actual'}
                            >
                                ⏭ Saltear {skipCount > 0 ? `(${skipCount})` : ''}
                            </button>
                        </div>
                    )}

                    {!gameWon && !selectedPlayer && (
                        <ul className="b11-tips">
                            <li>Cada acierto cambia el cruce por uno nuevo.</li>
                            <li>El jugador tiene que entrar en un puesto libre de la formación.</li>
                            <li><b>Saltear</b> cambia el cruce sin gastar el once.</li>
                        </ul>
                    )}

                    {gameWon && (
                        <div className="victory-screen">
                            <h2 className="victory-message">🏆 ¡Once completo!</h2>
                            <p className="b11-victory-sub">
                                Llenaste los {totalRequiredPlayers} puestos del {formationName}.
                            </p>
                            <div className="victory-buttons">
                                <button className="play-again-btn" onClick={resetGame}>
                                    Jugar de nuevo
                                </button>
                                <button
                                    className="share-btn"
                                    onClick={handleShareFormation}
                                    disabled={descargando}
                                >
                                    {descargando ? 'Generando…' : '⬇ Descargar'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlayerCombinerArg;
